// src/components/ItemManagement.js
import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Avatar,
  IconButton,
  InputAdornment,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Tabs,
  Tab,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Rating,
  Switch,
} from "@mui/material";

import {
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
  ShoppingCart,
  AttachMoney,
  TrendingUp,
  Image as ImageIcon,
  Inventory,
  Share,
  ExpandMore,
  Category,
  Palette,
  Straighten,
  LocalOffer,
  Clear,
  Search,
} from "@mui/icons-material";

import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { PRODUCT_CATEGORY_NAMES } from "../config/productCategories";

const ItemManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [newCategoryDialog, setNewCategoryDialog] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    brand: "",
    sizes: [],
    colors: [],
    weight: "",
    dimensions: "",
    specifications: "",
    basePrice: "",
    profitMargin: "",
    sellingPrice: "",
    gstPercentage: "18",
    imageUrl: "",
    additionalImages: [],
    stockQuantity: "",
    minOrderQuantity: "1",
    maxOrderQuantity: "",
    isActive: true,
    referralShare: false,
    tags: [],
    // New e-commerce fields
    sku: "",
    barcode: "",
    material: "",
    warranty: "",
    features: [],
    shippingWeight: "",
    shippingDimensions: "",
    countryOfOrigin: "India",
    manufacturer: "",
    isFragile: false,
    requiresAssembly: false,
    ageGroup: "",
    gender: "",
    season: "",
    style: "",
    pattern: "",
    occasion: "",
    careInstructions: "",
    ingredients: "",
    nutritionalInfo: "",
    allergenInfo: "",
    expiryMonths: "",
    storageConditions: "",
    certifications: [],
    rating: 0,
    videoUrl: "",
    threeDModelUrl: "",
    compareAtPrice: "",
    saleStartDate: "",
    saleEndDate: "",
    metaTitle: "",
    metaDescription: "",
    searchKeywords: [],
    relatedItems: [],
    bundleItems: [],
    shippingClass: "standard",
    customFields: {},
    hsnCode: "",
  });

  const categories = PRODUCT_CATEGORY_NAMES;

  const colorOptions = [
    "Red",
    "Blue",
    "Green",
    "Yellow",
    "Black",
    "White",
    "Gray",
    "Pink",
    "Purple",
    "Orange",
    "Brown",
    "Navy",
    "Maroon",
    "Beige",
    "Cream",
    "Gold",
    "Silver",
    "Rose Gold",
    "Multicolor",
    "Transparent",
  ];

  const sizeOptions = [
    "XS",
    "S",
    "M",
    "L",
    "XL",
    "XXL",
    "XXXL", // Clothing
    "6",
    "7",
    "8",
    "9",
    "10",
    "11",
    "12", // Shoes
    "28",
    "30",
    "32",
    "34",
    "36",
    "38",
    "40",
    "42", // Waist sizes
    "One Size",
    "Free Size",
    "Adjustable",
    "Small",
    "Medium",
    "Large",
    "Extra Large",
    "Mini",
    "Regular",
    "Maxi",
    "Plus Size",
  ];

  const brandOptions = [
    "Apple",
    "Samsung",
    "Nike",
    "Adidas",
    "Sony",
    "LG",
    "Dell",
    "HP",
    "Canon",
    "Nikon",
    "Puma",
    "Reebok",
    "H&M",
    "Zara",
    "Uniqlo",
    "Xiaomi",
    "OnePlus",
    "Realme",
    "Vivo",
    "Oppo",
    "Boat",
    "JBL",
    "Generic",
    "No Brand",
    "OEM",
    "Private Label",
  ];

  const shippingClasses = [
    { value: "standard", label: "Standard Shipping" },
    { value: "express", label: "Express Shipping" },
    { value: "overnight", label: "Overnight Shipping" },
    { value: "free", label: "Free Shipping" },
    { value: "heavy", label: "Heavy Item" },
    { value: "fragile", label: "Fragile Item" },
    { value: "cold_chain", label: "Cold Chain" },
    { value: "digital", label: "Digital Delivery" },
  ];

  // Fetch items from Firestore
  const fetchItems = async () => {
    try {
      setLoading(true);
      setError("");

      const itemsQuery = query(
        collection(db, "items"),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(itemsQuery);
      const itemsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setItems(itemsData);
    } catch (err) {
      console.error("Error fetching items:", err);
      setError("Failed to fetch items: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // Filter items based on search term
  const filteredItems = items.filter((item) => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const searchFields = [
      item.name || "",
      item.description || "",
      item.category || "",
      item.brand || "",
      item.sku || "",
      item.barcode || "",
      item.manufacturer || "",
      (item.searchKeywords || []).join(" "),
      (item.tags || []).join(" "),
    ];
    
    return searchFields.some((field) =>
      field.toLowerCase().includes(searchLower)
    );
  });

  // Calculate selling price when base price or profit margin changes
  useEffect(() => {
    if (formData.basePrice && formData.profitMargin) {
      const basePrice = parseFloat(formData.basePrice);
      const profitMargin = parseFloat(formData.profitMargin);

      if (!isNaN(basePrice) && !isNaN(profitMargin)) {
        const sellingPrice = basePrice + profitMargin;
        setFormData((prev) => ({
          ...prev,
          sellingPrice: sellingPrice.toFixed(2),
        }));
      }
    }
  }, [formData.basePrice, formData.profitMargin]);

  // Auto-generate SKU when name changes
  useEffect(() => {
    if (formData.name && !editingItem) {
      const sku =
        formData.name
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, "")
          .substring(0, 8) + Date.now().toString().slice(-4);
      setFormData((prev) => ({
        ...prev,
        sku: sku,
      }));
    }
  }, [formData.name, editingItem]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleArrayFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleAddImageUrl = () => {
    const url = prompt("Enter image URL:");
    if (url && url.trim()) {
      setFormData((prev) => ({
        ...prev,
        additionalImages: [...prev.additionalImages, url.trim()],
      }));
    }
  };

  const handleRemoveImageUrl = (index) => {
    setFormData((prev) => ({
      ...prev,
      additionalImages: prev.additionalImages.filter((_, i) => i !== index),
    }));
  };

  const handleAddNewCategory = () => {
    if (newCategory.trim() && !categories.includes(newCategory.trim())) {
      categories.push(newCategory.trim());
      setFormData((prev) => ({
        ...prev,
        category: newCategory.trim(),
      }));
      setNewCategory("");
      setNewCategoryDialog(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      category: "",
      brand: "",
      sizes: [],
      colors: [],
      weight: "",
      dimensions: "",
      specifications: "",
      basePrice: "",
      profitMargin: "",
      sellingPrice: "",
      gstPercentage: "18",
      imageUrl: "",
      additionalImages: [],
      stockQuantity: "",
      minOrderQuantity: "1",
      maxOrderQuantity: "",
      isActive: true,
      referralShare: false,
      tags: [],
      sku: "",
      barcode: "",
      material: "",
      warranty: "",
      features: [],
      shippingWeight: "",
      shippingDimensions: "",
      countryOfOrigin: "India",
      manufacturer: "",
      isFragile: false,
      requiresAssembly: false,
      ageGroup: "",
      gender: "",
      season: "",
      style: "",
      pattern: "",
      occasion: "",
      careInstructions: "",
      ingredients: "",
      nutritionalInfo: "",
      allergenInfo: "",
      expiryMonths: "",
      storageConditions: "",
      certifications: [],
      rating: 0,
      videoUrl: "",
      threeDModelUrl: "",
      compareAtPrice: "",
      saleStartDate: "",
      saleEndDate: "",
      metaTitle: "",
      metaDescription: "",
      searchKeywords: [],
      relatedItems: [],
      bundleItems: [],
      shippingClass: "standard",
      customFields: {},
      hsnCode: "",
    });
    setEditingItem(null);
    setActiveTab(0);
  };

  const handleAddItem = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleEditItem = (item) => {
    setFormData({
      name: item.name || "",
      description: item.description || "",
      category: item.category || "",
      brand: item.brand || "",
      sizes: item.sizes || [],
      colors: item.colors || [],
      weight: item.weight || "",
      dimensions: item.dimensions || "",
      specifications: item.specifications || "",
      basePrice: item.basePrice?.toString() || "",
      profitMargin: item.profitMargin?.toString() || "",
      sellingPrice: item.sellingPrice?.toString() || "",
      gstPercentage: (item.gstPercentage !== undefined && item.gstPercentage !== null) ? item.gstPercentage.toString() : "18",
      imageUrl: item.imageUrl || "",
      additionalImages: item.additionalImages || [],
      stockQuantity: item.stockQuantity?.toString() || "",
      minOrderQuantity: item.minOrderQuantity?.toString() || "1",
      maxOrderQuantity: item.maxOrderQuantity?.toString() || "",
      isActive: item.isActive !== undefined ? item.isActive : true,
      referralShare: item.referralShare || false,
      tags: item.tags || [],
      sku: item.sku || "",
      barcode: item.barcode || "",
      material: item.material || "",
      warranty: item.warranty || "",
      features: item.features || [],
      shippingWeight: item.shippingWeight || "",
      shippingDimensions: item.shippingDimensions || "",
      countryOfOrigin: item.countryOfOrigin || "India",
      manufacturer: item.manufacturer || "",
      isFragile: item.isFragile || false,
      requiresAssembly: item.requiresAssembly || false,
      ageGroup: item.ageGroup || "",
      gender: item.gender || "",
      season: item.season || "",
      style: item.style || "",
      pattern: item.pattern || "",
      occasion: item.occasion || "",
      careInstructions: item.careInstructions || "",
      ingredients: item.ingredients || "",
      nutritionalInfo: item.nutritionalInfo || "",
      allergenInfo: item.allergenInfo || "",
      expiryMonths: item.expiryMonths || "",
      storageConditions: item.storageConditions || "",
      certifications: item.certifications || [],
      rating: item.rating || 0,
      videoUrl: item.videoUrl || "",
      threeDModelUrl: item.threeDModelUrl || "",
      compareAtPrice: item.compareAtPrice?.toString() || "",
      saleStartDate: item.saleStartDate || "",
      saleEndDate: item.saleEndDate || "",
      metaTitle: item.metaTitle || "",
      metaDescription: item.metaDescription || "",
      searchKeywords: item.searchKeywords || [],
      relatedItems: item.relatedItems || [],
      bundleItems: item.bundleItems || [],
      shippingClass: item.shippingClass || "standard",
      customFields: item.customFields || {},
      hsnCode: item.hsnCode || "",
    });
    setEditingItem(item);
    setDialogOpen(true);
  };

  const handleSaveItem = async () => {
    try {
      setError("");
      setSuccess("");

      // Validation
      if (!formData.name.trim()) {
        setError("Item name is required");
        return;
      }

      if (!formData.description.trim()) {
        setError("Description is required");
        return;
      }

      if (!formData.category) {
        setError("Category is required");
        return;
      }

      if (!formData.basePrice || parseFloat(formData.basePrice) <= 0) {
        setError("Valid base price is required");
        return;
      }

      if (!formData.profitMargin || parseFloat(formData.profitMargin) < 0) {
        setError("Valid profit margin is required");
        return;
      }

      if (!formData.gstPercentage || parseFloat(formData.gstPercentage) < 0 || parseFloat(formData.gstPercentage) > 100) {
        setError("GST percentage must be between 0 and 100");
        return;
      }

      if (!formData.stockQuantity || parseInt(formData.stockQuantity) < 0) {
        setError("Valid stock quantity is required");
        return;
      }

      if (formData.sku && formData.sku.length < 3) {
        setError("SKU must be at least 3 characters long");
        return;
      }

      const itemData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category,
        brand: formData.brand.trim(),
        sizes: formData.sizes,
        colors: formData.colors,
        weight: formData.weight.trim(),
        dimensions: formData.dimensions.trim(),
        specifications: formData.specifications.trim(),
        basePrice: parseFloat(formData.basePrice),
        profitMargin: parseFloat(formData.profitMargin),
        sellingPrice: parseFloat(formData.sellingPrice),
        gstPercentage: parseFloat(formData.gstPercentage),
        imageUrl: formData.imageUrl.trim(),
        additionalImages: formData.additionalImages,
        stockQuantity: parseInt(formData.stockQuantity),
        minOrderQuantity: parseInt(formData.minOrderQuantity) || 1,
        maxOrderQuantity: formData.maxOrderQuantity
          ? parseInt(formData.maxOrderQuantity)
          : null,
        isActive: formData.isActive,
        referralShare: formData.referralShare,
        tags: formData.tags,
        // New fields
        sku: formData.sku.trim(),
        barcode: formData.barcode.trim(),
        material: formData.material.trim(),
        warranty: formData.warranty.trim(),
        features: formData.features,
        shippingWeight: formData.shippingWeight.trim(),
        shippingDimensions: formData.shippingDimensions.trim(),
        countryOfOrigin: formData.countryOfOrigin,
        manufacturer: formData.manufacturer.trim(),
        isFragile: formData.isFragile,
        requiresAssembly: formData.requiresAssembly,
        ageGroup: formData.ageGroup,
        gender: formData.gender,
        season: formData.season,
        style: formData.style,
        pattern: formData.pattern,
        occasion: formData.occasion,
        careInstructions: formData.careInstructions.trim(),
        ingredients: formData.ingredients.trim(),
        nutritionalInfo: formData.nutritionalInfo.trim(),
        allergenInfo: formData.allergenInfo.trim(),
        expiryMonths: formData.expiryMonths
          ? parseInt(formData.expiryMonths)
          : null,
        storageConditions: formData.storageConditions.trim(),
        certifications: formData.certifications,
        rating: formData.rating,
        videoUrl: formData.videoUrl.trim(),
        threeDModelUrl: formData.threeDModelUrl.trim(),
        compareAtPrice: formData.compareAtPrice
          ? parseFloat(formData.compareAtPrice)
          : null,
        saleStartDate: formData.saleStartDate,
        saleEndDate: formData.saleEndDate,
        metaTitle: formData.metaTitle.trim(),
        metaDescription: formData.metaDescription.trim(),
        searchKeywords: formData.searchKeywords,
        relatedItems: formData.relatedItems,
        bundleItems: formData.bundleItems,
        shippingClass: formData.shippingClass,
        customFields: formData.customFields,
        hsnCode: formData.hsnCode.trim(),
        updatedAt: new Date().toISOString(),
      };

      if (editingItem) {
        // Update existing item
        await updateDoc(doc(db, "items", editingItem.id), itemData);
        setSuccess("Item updated successfully!");
      } else {
        // Add new item
        itemData.createdAt = new Date().toISOString();
        await addDoc(collection(db, "items"), itemData);
        setSuccess("Item added successfully!");
      }

      setDialogOpen(false);
      resetForm();
      fetchItems();
    } catch (err) {
      console.error("Error saving item:", err);
      setError("Failed to save item: " + err.message);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        await deleteDoc(doc(db, "items", itemId));
        setSuccess("Item deleted successfully!");
        fetchItems();
      } catch (err) {
        console.error("Error deleting item:", err);
        setError("Failed to delete item: " + err.message);
      }
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await updateDoc(doc(db, "items", item.id), {
        isActive: !item.isActive,
        updatedAt: new Date().toISOString(),
      });
      setSuccess(
        `Item ${!item.isActive ? "activated" : "deactivated"} successfully!`
      );
      fetchItems();
    } catch (err) {
      console.error("Error updating item status:", err);
      setError("Failed to update item status: " + err.message);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5">Item Management</Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flexGrow: 1, justifyContent: "flex-end" }}>
          <TextField
            placeholder="Search items by name, SKU, brand, category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ minWidth: 300, maxWidth: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm("")}
                    edge="end"
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" startIcon={<Add />} onClick={handleAddItem}>
            Add New Item
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box textAlign="center" sx={{ py: 4 }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading items...
          </Typography>
        </Box>
      ) : items.length === 0 ? (
        <Card sx={{ textAlign: "center", py: 6 }}>
          <CardContent>
            <Inventory color="disabled" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Items Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start by adding your first item to the inventory.
            </Typography>
          </CardContent>
        </Card>
      ) : filteredItems.length === 0 ? (
        <Card sx={{ textAlign: "center", py: 6 }}>
          <CardContent>
            <Search color="disabled" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Items Match Your Search
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Try searching with different keywords or clear the search to see all items.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Clear />}
              onClick={() => setSearchTerm("")}
            >
              Clear Search
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {searchTerm && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Found {filteredItems.length} item{filteredItems.length !== 1 ? "s" : ""} matching "{searchTerm}"
            </Alert>
          )}
          <Grid container spacing={3}>
            {filteredItems.map((item) => (
            <Grid item xs={12} md={6} lg={4} key={item.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  opacity: item.isActive ? 1 : 0.6,
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}
                  >
                    <Avatar
                      src={item.imageUrl}
                      sx={{ width: 60, height: 60, mr: 2 }}
                    >
                      <ShoppingCart />
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {item.name}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          mb: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Chip
                          label={item.category}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        {item.brand && (
                          <Chip
                            label={item.brand}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                        {item.sku && (
                          <Chip
                            label={`SKU: ${item.sku}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Chip
                        label={item.isActive ? "Active" : "Inactive"}
                        size="small"
                        color={item.isActive ? "success" : "default"}
                      />
                    </Box>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {item.description}
                  </Typography>

                  {/* Additional Info */}
                  <Box sx={{ mb: 2 }}>
                    {item.sizes && item.sizes.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Sizes: {item.sizes.join(", ")}
                      </Typography>
                    )}
                    {item.colors && item.colors.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Colors: {item.colors.join(", ")}
                      </Typography>
                    )}
                    {item.material && (
                      <Typography variant="body2" color="text.secondary">
                        Material: {item.material}
                      </Typography>
                    )}
                    {item.warranty && (
                      <Typography variant="body2" color="text.secondary">
                        Warranty: {item.warranty}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Base Price: {formatPrice(item.basePrice)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Profit Margin: ₹{item.profitMargin}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      GST: {(item.gstPercentage !== undefined && item.gstPercentage !== null && item.gstPercentage !== '') ? Number(item.gstPercentage) : 18}%
                    </Typography>
                    <Typography variant="h6" color="primary">
                      Selling Price: {formatPrice(item.sellingPrice)}
                    </Typography>
                    {item.compareAtPrice && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textDecoration: "line-through" }}
                      >
                        Compare: {formatPrice(item.compareAtPrice)}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      Stock: {item.stockQuantity} units
                    </Typography>
                    {item.referralShare && (
                      <Chip
                        label="Referral points"
                        size="small"
                        color="secondary"
                        icon={<Share />}
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => handleEditItem(item)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color={item.isActive ? "warning" : "success"}
                    onClick={() => handleToggleActive(item)}
                  >
                    {item.isActive ? "Deactivate" : "Activate"}
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => handleDeleteItem(item.id)}
                  >
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        </>
      )}

      {/* Add/Edit Item Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: "90vh" },
        }}
      >
        <DialogTitle>{editingItem ? "Edit Item" : "Add New Item"}</DialogTitle>
        <DialogContent sx={{ overflow: "auto" }}>
          <Box sx={{ width: "100%" }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
            >
              <Tab label="Basic Info" />
              <Tab label="Pricing & Stock" />
              <Tab label="Attributes" />
              <Tab label="Media & SEO" />
              <Tab label="Shipping & Legal" />
            </Tabs>

            {/* Basic Info Tab */}
            {activeTab === 0 && (
              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="Item Name"
                      value={formData.name}
                      onChange={handleInputChange("name")}
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ShoppingCart />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="SKU"
                      value={formData.sku}
                      onChange={handleInputChange("sku")}
                      helperText="Auto-generated or enter custom"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={formData.category}
                        onChange={handleInputChange("category")}
                        label="Category"
                        startAdornment={
                          <InputAdornment position="start">
                            <Category />
                          </InputAdornment>
                        }
                      >
                        {categories.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))}
                        <MenuItem onClick={() => setNewCategoryDialog(true)}>
                          <Add sx={{ mr: 1 }} /> Add New Category
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      freeSolo
                      options={brandOptions}
                      value={formData.brand}
                      onChange={(event, newValue) => {
                        setFormData((prev) => ({
                          ...prev,
                          brand: newValue || "",
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Brand"
                          helperText="Select existing or type new brand"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={formData.description}
                      onChange={handleInputChange("description")}
                      multiline
                      rows={4}
                      required
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      multiple
                      options={sizeOptions}
                      value={formData.sizes}
                      onChange={(event, newValue) => {
                        handleArrayFieldChange("sizes", newValue);
                      }}
                      freeSolo
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option}
                            {...getTagProps({ index })}
                            key={index}
                          />
                        ))
                      }
                      renderInput={(params) => {
                        const { InputProps, ...restParams } = params;
                        return (
                          <TextField
                            {...restParams}
                            label="Sizes Available"
                            placeholder="Select or type sizes"
                            InputProps={{
                              ...InputProps,
                              startAdornment: (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <InputAdornment position="start">
                                    <Straighten />
                                  </InputAdornment>
                                  {InputProps.startAdornment}
                                </Box>
                              ),
                            }}
                          />
                        );
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      multiple
                      options={colorOptions}
                      value={formData.colors}
                      onChange={(event, newValue) => {
                        handleArrayFieldChange("colors", newValue);
                      }}
                      freeSolo
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option}
                            {...getTagProps({ index })}
                            key={index}
                            sx={{
                              backgroundColor:
                                option.toLowerCase() === "red"
                                  ? "#ffebee"
                                  : option.toLowerCase() === "blue"
                                  ? "#F0F8FF"
                                  : option.toLowerCase() === "green"
                                  ? "#e8f5e8"
                                  : option.toLowerCase() === "yellow"
                                  ? "#fffde7"
                                  : option.toLowerCase() === "black"
                                  ? "#fafafa"
                                  : "inherit",
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => {
                        const { InputProps, ...restParams } = params;
                        return (
                          <TextField
                            {...restParams}
                            label="Colors Available"
                            placeholder="Select or type colors"
                            InputProps={{
                              ...InputProps,
                              startAdornment: (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <InputAdornment position="start">
                                    <Palette />
                                  </InputAdornment>
                                  {InputProps.startAdornment}
                                </Box>
                              ),
                            }}
                          />
                        );
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Material"
                      value={formData.material}
                      onChange={handleInputChange("material")}
                      placeholder="e.g., Cotton, Polyester, Plastic, Metal"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Manufacturer"
                      value={formData.manufacturer}
                      onChange={handleInputChange("manufacturer")}
                      placeholder="Manufacturing company"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Weight"
                      value={formData.weight}
                      onChange={handleInputChange("weight")}
                      placeholder="e.g., 500g, 1.2kg"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Dimensions"
                      value={formData.dimensions}
                      onChange={handleInputChange("dimensions")}
                      placeholder="L x W x H (e.g., 20x15x5 cm)"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Barcode"
                      value={formData.barcode}
                      onChange={handleInputChange("barcode")}
                      placeholder="Product barcode/UPC"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={formData.tags}
                      onChange={(event, newValue) => {
                        handleArrayFieldChange("tags", newValue);
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option}
                            {...getTagProps({ index })}
                            key={index}
                            color="primary"
                          />
                        ))
                      }
                      renderInput={(params) => {
                        const { InputProps, ...restParams } = params;
                        return (
                          <TextField
                            {...restParams}
                            label="Tags"
                            placeholder="Add tags for better searchability"
                            InputProps={{
                              ...InputProps,
                              startAdornment: (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <InputAdornment position="start">
                                    <LocalOffer />
                                  </InputAdornment>
                                  {InputProps.startAdornment}
                                </Box>
                              ),
                            }}
                          />
                        );
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Pricing & Stock Tab */}
            {activeTab === 1 && (
              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Base Price (₹)"
                      type="number"
                      value={formData.basePrice}
                      onChange={handleInputChange("basePrice")}
                      required
                      inputProps={{ min: 0, step: 0.01 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoney />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Profit Margin (₹)"
                      type="number"
                      value={formData.profitMargin}
                      onChange={handleInputChange("profitMargin")}
                      required
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Enter profit amount in rupees"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <TrendingUp />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Selling Price (₹)"
                      type="number"
                      value={formData.sellingPrice}
                      disabled
                      helperText="Auto-calculated: Base Price + Profit Margin"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoney />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="GST Percentage (%)"
                      type="number"
                      value={formData.gstPercentage}
                      onChange={handleInputChange("gstPercentage")}
                      required
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      helperText="GST tax percentage (e.g., 18 for 18%)"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            %
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="HSN Code"
                      value={formData.hsnCode}
                      onChange={handleInputChange("hsnCode")}
                      placeholder="e.g., 6109"
                      helperText="Harmonized System of Nomenclature code"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Compare At Price (₹)"
                      type="number"
                      value={formData.compareAtPrice}
                      onChange={handleInputChange("compareAtPrice")}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Original price for sale display"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Stock Quantity"
                      type="number"
                      value={formData.stockQuantity}
                      onChange={handleInputChange("stockQuantity")}
                      required
                      inputProps={{ min: 0 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Inventory />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Min Order Quantity"
                      type="number"
                      value={formData.minOrderQuantity}
                      onChange={handleInputChange("minOrderQuantity")}
                      inputProps={{ min: 1 }}
                      helperText="Minimum quantity per order"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Max Order Quantity"
                      type="number"
                      value={formData.maxOrderQuantity}
                      onChange={handleInputChange("maxOrderQuantity")}
                      inputProps={{ min: 1 }}
                      helperText="Maximum quantity per order (optional)"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={formData.isActive}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            isActive: e.target.value,
                          }))
                        }
                        label="Status"
                      >
                        <MenuItem value={true}>Active</MenuItem>
                        <MenuItem value={false}>Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Sale Dates */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Sale Start Date"
                      type="datetime-local"
                      value={formData.saleStartDate}
                      onChange={handleInputChange("saleStartDate")}
                      InputLabelProps={{ shrink: true }}
                      helperText="Start date for sale pricing"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Sale End Date"
                      type="datetime-local"
                      value={formData.saleEndDate}
                      onChange={handleInputChange("saleEndDate")}
                      InputLabelProps={{ shrink: true }}
                      helperText="End date for sale pricing"
                    />
                  </Grid>

                  {/* Referral Share Section */}
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        p: 2,
                        bgcolor: "grey.50",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ fontWeight: 600 }}
                      >
                        Referral Settings
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.referralShare}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                referralShare: e.target.checked,
                              }))
                            }
                            color="secondary"
                            size="medium"
                          />
                        }
                        label={
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              Enable Referral Share
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Check this if referrers should earn Points on
                              this item
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  </Grid>

                  {/* Profit Calculation Summary */}
                  {formData.basePrice && formData.profitMargin && (
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ bgcolor: "primary.50" }}>
                        <CardContent>
                          <Typography variant="subtitle2" gutterBottom>
                            Profit Calculation Summary:
                          </Typography>
                          <Typography variant="body2">
                            Base Price:{" "}
                            {formatPrice(parseFloat(formData.basePrice) || 0)}
                          </Typography>
                          <Typography variant="body2">
                            Profit Amount: ₹
                            {parseFloat(formData.profitMargin) || 0}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold" }}
                          >
                            Final Selling Price:{" "}
                            {formatPrice(
                              parseFloat(formData.sellingPrice) || 0
                            )}
                          </Typography>
                          {formData.compareAtPrice && (
                            <Typography variant="body2" color="success.main">
                              Discount:{" "}
                              {(
                                ((parseFloat(formData.compareAtPrice) -
                                  parseFloat(formData.sellingPrice)) /
                                  parseFloat(formData.compareAtPrice)) *
                                100
                              ).toFixed(1)}
                              % off
                            </Typography>
                          )}
                          {formData.referralShare && (
                            <Typography variant="body2" color="secondary.main">
                              ✓ This item includes referral share
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* Attributes Tab */}
            {activeTab === 2 && (
              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Warranty"
                      value={formData.warranty}
                      onChange={handleInputChange("warranty")}
                      placeholder="e.g., 1 year, 6 months, No warranty"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Country of Origin</InputLabel>
                      <Select
                        value={formData.countryOfOrigin}
                        onChange={handleInputChange("countryOfOrigin")}
                        label="Country of Origin"
                      >
                        <MenuItem value="India">India</MenuItem>
                        <MenuItem value="China">China</MenuItem>
                        <MenuItem value="USA">USA</MenuItem>
                        <MenuItem value="Germany">Germany</MenuItem>
                        <MenuItem value="Japan">Japan</MenuItem>
                        <MenuItem value="South Korea">South Korea</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Age Group</InputLabel>
                      <Select
                        value={formData.ageGroup}
                        onChange={handleInputChange("ageGroup")}
                        label="Age Group"
                      >
                        <MenuItem value="">Not Applicable</MenuItem>
                        <MenuItem value="0-2 years">0-2 years</MenuItem>
                        <MenuItem value="3-5 years">3-5 years</MenuItem>
                        <MenuItem value="6-12 years">6-12 years</MenuItem>
                        <MenuItem value="13-17 years">13-17 years</MenuItem>
                        <MenuItem value="18+ years">18+ years</MenuItem>
                        <MenuItem value="All ages">All ages</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Gender</InputLabel>
                      <Select
                        value={formData.gender}
                        onChange={handleInputChange("gender")}
                        label="Gender"
                      >
                        <MenuItem value="">Unisex</MenuItem>
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Kids">Kids</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Season</InputLabel>
                      <Select
                        value={formData.season}
                        onChange={handleInputChange("season")}
                        label="Season"
                      >
                        <MenuItem value="">All Seasons</MenuItem>
                        <MenuItem value="Spring">Spring</MenuItem>
                        <MenuItem value="Summer">Summer</MenuItem>
                        <MenuItem value="Monsoon">Monsoon</MenuItem>
                        <MenuItem value="Winter">Winter</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Style"
                      value={formData.style}
                      onChange={handleInputChange("style")}
                      placeholder="e.g., Casual, Formal, Vintage, Modern"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Pattern"
                      value={formData.pattern}
                      onChange={handleInputChange("pattern")}
                      placeholder="e.g., Solid, Striped, Floral, Geometric"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Occasion"
                      value={formData.occasion}
                      onChange={handleInputChange("occasion")}
                      placeholder="e.g., Daily wear, Party, Wedding, Sports"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.isFragile}
                            onChange={handleCheckboxChange("isFragile")}
                          />
                        }
                        label="Fragile Item"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.requiresAssembly}
                            onChange={handleCheckboxChange("requiresAssembly")}
                          />
                        }
                        label="Requires Assembly"
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={formData.features}
                      onChange={(event, newValue) => {
                        handleArrayFieldChange("features", newValue);
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="filled"
                            label={option}
                            {...getTagProps({ index })}
                            key={index}
                            color="success"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Key Features"
                          placeholder="Add product features and highlights"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Care Instructions"
                      value={formData.careInstructions}
                      onChange={handleInputChange("careInstructions")}
                      multiline
                      rows={3}
                      placeholder="How to care for this product..."
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Detailed Specifications"
                      value={formData.specifications}
                      onChange={handleInputChange("specifications")}
                      multiline
                      rows={4}
                      placeholder="Technical specifications, detailed features, etc."
                    />
                  </Grid>

                  {/* Food & Consumables Section */}
                  <Grid item xs={12}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle2">
                          Food & Consumables (if applicable)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Ingredients"
                              value={formData.ingredients}
                              onChange={handleInputChange("ingredients")}
                              multiline
                              rows={2}
                              placeholder="List of ingredients..."
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Nutritional Information"
                              value={formData.nutritionalInfo}
                              onChange={handleInputChange("nutritionalInfo")}
                              multiline
                              rows={2}
                              placeholder="Nutrition facts..."
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Allergen Information"
                              value={formData.allergenInfo}
                              onChange={handleInputChange("allergenInfo")}
                              placeholder="Contains nuts, dairy, etc."
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Expiry (Months)"
                              type="number"
                              value={formData.expiryMonths}
                              onChange={handleInputChange("expiryMonths")}
                              inputProps={{ min: 0 }}
                              placeholder="Shelf life in months"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Storage Conditions"
                              value={formData.storageConditions}
                              onChange={handleInputChange("storageConditions")}
                              placeholder="Store in cool, dry place"
                            />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[
                        "ISO 9001",
                        "CE Mark",
                        "FDA Approved",
                        "BIS",
                        "ISI Mark",
                        "FSSAI",
                        "Organic Certified",
                      ]}
                      value={formData.certifications}
                      onChange={(event, newValue) => {
                        handleArrayFieldChange("certifications", newValue);
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option}
                            {...getTagProps({ index })}
                            key={index}
                            color="warning"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Certifications"
                          placeholder="Product certifications and standards"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Media & SEO Tab */}
            {activeTab === 3 && (
              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Main Image URL"
                      value={formData.imageUrl}
                      onChange={handleInputChange("imageUrl")}
                      placeholder="https://example.com/image.jpg"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ImageIcon />
                          </InputAdornment>
                        ),
                      }}
                      helperText="Enter a direct URL to the main product image"
                    />
                  </Grid>

                  {formData.imageUrl && (
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: "center", mt: 2 }}>
                        <Typography variant="body2" gutterBottom>
                          Main Image Preview:
                        </Typography>
                        <img
                          src={formData.imageUrl}
                          alt="Item preview"
                          style={{
                            maxWidth: "200px",
                            maxHeight: "200px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "1px solid #ddd",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Typography variant="subtitle2">
                        Additional Images
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Add />}
                        onClick={handleAddImageUrl}
                      >
                        Add Image URL
                      </Button>
                    </Box>
                    {formData.additionalImages.length > 0 && (
                      <List dense>
                        {formData.additionalImages.map((url, index) => (
                          <ListItem key={index} sx={{ pl: 0 }}>
                            <ListItemText
                              primary={url}
                              secondary={`Image ${index + 1}`}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                onClick={() => handleRemoveImageUrl(index)}
                                size="small"
                                color="error"
                              >
                                <Clear />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Video URL"
                      value={formData.videoUrl}
                      onChange={handleInputChange("videoUrl")}
                      placeholder="YouTube, Vimeo, or direct video URL"
                      helperText="Product demo or promotional video"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="3D Model URL"
                      value={formData.threeDModelUrl}
                      onChange={handleInputChange("threeDModelUrl")}
                      placeholder="3D model or AR view URL"
                      helperText="3D model for interactive viewing"
                    />
                  </Grid>

                  <Divider sx={{ width: "100%", my: 2 }} />

                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      SEO & Search Optimization
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Meta Title"
                      value={formData.metaTitle}
                      onChange={handleInputChange("metaTitle")}
                      placeholder="SEO title for search engines"
                      helperText={`${formData.metaTitle.length}/60 characters`}
                      inputProps={{ maxLength: 60 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Meta Description"
                      value={formData.metaDescription}
                      onChange={handleInputChange("metaDescription")}
                      multiline
                      rows={2}
                      placeholder="SEO description for search engines"
                      helperText={`${formData.metaDescription.length}/160 characters`}
                      inputProps={{ maxLength: 160 }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={formData.searchKeywords}
                      onChange={(event, newValue) => {
                        handleArrayFieldChange("searchKeywords", newValue);
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option}
                            {...getTagProps({ index })}
                            key={index}
                            color="info"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search Keywords"
                          placeholder="Keywords customers might search for"
                          helperText="Add keywords to improve product discoverability"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Product Rating
                      </Typography>
                      <Rating
                        value={formData.rating}
                        onChange={(event, newValue) => {
                          setFormData((prev) => ({
                            ...prev,
                            rating: newValue || 0,
                          }));
                        }}
                        precision={0.5}
                        size="large"
                      />
                      <Typography variant="body2" color="text.secondary">
                        Default rating for new products
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Shipping & Legal Tab */}
            {activeTab === 4 && (
              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Shipping Information
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Shipping Weight"
                      value={formData.shippingWeight}
                      onChange={handleInputChange("shippingWeight")}
                      placeholder="e.g., 1.5kg"
                      helperText="Weight for shipping calculation"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Shipping Dimensions"
                      value={formData.shippingDimensions}
                      onChange={handleInputChange("shippingDimensions")}
                      placeholder="L x W x H (packed)"
                      helperText="Packed dimensions for shipping"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Shipping Class</InputLabel>
                      <Select
                        value={formData.shippingClass}
                        onChange={handleInputChange("shippingClass")}
                        label="Shipping Class"
                      >
                        {shippingClasses.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Divider sx={{ width: "100%", my: 2 }} />

                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Related Products & Bundles
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={items.map((item) => ({
                        label: item.name,
                        id: item.id,
                      }))}
                      value={formData.relatedItems}
                      onChange={(event, newValue) => {
                        handleArrayFieldChange("relatedItems", newValue);
                      }}
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.label
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={
                              typeof option === "string" ? option : option.label
                            }
                            {...getTagProps({ index })}
                            key={index}
                            color="primary"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Related Items"
                          placeholder="Select related products"
                          helperText="Products to suggest to customers"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={items.map((item) => ({
                        label: item.name,
                        id: item.id,
                      }))}
                      value={formData.bundleItems}
                      onChange={(event, newValue) => {
                        handleArrayFieldChange("bundleItems", newValue);
                      }}
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.label
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="filled"
                            label={
                              typeof option === "string" ? option : option.label
                            }
                            {...getTagProps({ index })}
                            key={index}
                            color="secondary"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Bundle Items"
                          placeholder="Select items for bundle deals"
                          helperText="Items that can be bundled together"
                        />
                      )}
                    />
                  </Grid>

                  <Divider sx={{ width: "100%", my: 2 }} />

                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Legal & Compliance
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        p: 2,
                        bgcolor: "grey.50",
                      }}
                    >
                      <Typography variant="subtitle2" gutterBottom>
                        Product Compliance
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                formData.customFields.ageVerificationRequired ||
                                false
                              }
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  customFields: {
                                    ...prev.customFields,
                                    ageVerificationRequired: e.target.checked,
                                  },
                                }))
                              }
                            />
                          }
                          label="Age Verification Required (18+)"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                formData.customFields.prescriptionRequired ||
                                false
                              }
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  customFields: {
                                    ...prev.customFields,
                                    prescriptionRequired: e.target.checked,
                                  },
                                }))
                              }
                            />
                          }
                          label="Prescription Required"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                formData.customFields.hazardousMaterial || false
                              }
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  customFields: {
                                    ...prev.customFields,
                                    hazardousMaterial: e.target.checked,
                                  },
                                }))
                              }
                            />
                          }
                          label="Hazardous Material"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                formData.customFields.importRestricted || false
                              }
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  customFields: {
                                    ...prev.customFields,
                                    importRestricted: e.target.checked,
                                  },
                                }))
                              }
                            />
                          }
                          label="Import Restricted"
                        />
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle2">
                          Additional Custom Fields
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Custom Field 1"
                              value={formData.customFields.custom1 || ""}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  customFields: {
                                    ...prev.customFields,
                                    custom1: e.target.value,
                                  },
                                }))
                              }
                              placeholder="Additional product information"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Custom Field 2"
                              value={formData.customFields.custom2 || ""}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  customFields: {
                                    ...prev.customFields,
                                    custom2: e.target.value,
                                  },
                                }))
                              }
                              placeholder="Additional product information"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Custom Notes"
                              value={formData.customFields.notes || ""}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  customFields: {
                                    ...prev.customFields,
                                    notes: e.target.value,
                                  },
                                }))
                              }
                              multiline
                              rows={3}
                              placeholder="Internal notes about this product"
                            />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0" }}>
          <Button onClick={() => setDialogOpen(false)} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveItem}
            variant="contained"
            startIcon={<Save />}
            size="large"
          >
            {editingItem ? "Update Item" : "Add Item"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Category Dialog */}
      <Dialog
        open={newCategoryDialog}
        onClose={() => setNewCategoryDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Category</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Category Name"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            autoFocus
            margin="normal"
            placeholder="Enter new category name"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewCategoryDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddNewCategory}
            variant="contained"
            disabled={!newCategory.trim()}
          >
            Add Category
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ItemManagement;
